import { createContext, useContext } from 'react';

export interface User {
  id: string;
  impersonated?: {
    originalEmail: string;
  };
  email: string;
  isInternal: boolean;
  emailDerivedName: string;
  fullName: string;
  isLoggedIn: boolean;
}

export interface Account {
  id: string;
}

export interface Tokens {
  accessToken: string;
  idToken: string;
}

export interface LoginOptions {
  loginWithRedirect: () => Promise<void>;
  loginWithPopup: () => Promise<void>;
  loginSilently: () => Promise<void>;
}

export interface AuthContextData {
  user: User | null;
  /**
   * Strictly speaking this is not part of the Auth flow. It is a handy shortcut to avoid having a separate AccountsContext.
   */
  personalAccount: Account | null;
  tokens: Tokens | null;
  isLoading: boolean;
  login: LoginOptions;
}

export const AuthContext = createContext<AuthContextData | null>(null);

export function getTargetUserInfo(
  isImpersonated: boolean,
  userEmail: string,
  impersonatedEmail?: string | null,
) {
  if (isImpersonated) {
    return {
      email: impersonatedEmail ?? '',
      impersonated: {
        originalEmail: userEmail,
      },
    };
  }
  return {
    email: userEmail,
  };
}

export function useAuth() {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useAuth must be used within an AuthContext');
  }

  return authContext;
}
